import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import type {
  ElementType,
  ForwardedRef,
  InputHTMLAttributes,
  ReactNode,
} from "react";
import { forwardRef } from "react";

export interface InputBaseProps extends InputHTMLAttributes<HTMLInputElement> {
  type:
    | "email"
    | "hidden"
    | "number"
    | "password"
    | "search"
    | "tel"
    | "text"
    | "url";
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  fullWidth?: boolean;
  name?: string;
  radius?: "none" | "rounded" | "full";
  dense?: boolean;
  inputSize?: "md" | "lg";
  label?: string;
  WrapperComponent?: ElementType;
  classNames?: {
    wrapper?: string;
    label?: string;
    input?: string;
    hint?: string;
    inputWrapper?: string;
  };
  hint?: string;
  HintComponent?: ElementType;
  hintId?: string;
}

const inputStyle = cva("action-focus w-full border bg-transparent", {
  variants: {
    radius: {
      none: "rounded-none",
      rounded: "rounded-lg",
      full: "rounded-full",
    },
    dense: {
      true: "p-2",
      false: "p-4",
    },
    size: {
      md: " text-lg",
      lg: "text-lg",
    },
  },
});

export const Input = forwardRef(
  (
    {
      name,
      startAdornment = null,
      endAdornment = null,
      fullWidth = false,
      inputSize = "md",
      type,
      dense = false,
      label = "",
      classNames,
      radius = "none",
      WrapperComponent = "div",
      hint,
      HintComponent = "div",
      hintId,
      ...props
    }: InputBaseProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => (
    <WrapperComponent
      className={cn("inline-block", fullWidth && "w-full", classNames?.wrapper)}
    >
      {label && (
        <label
          className={cn("mb-1 block text-base", classNames?.label)}
          htmlFor={name}
        >
          {label} {props.required && "*"}
        </label>
      )}
      <div className={cn("relative w-full", classNames?.inputWrapper)}>
        {startAdornment}
        <input
          ref={ref}
          type={type}
          name={name}
          className={cn(
            inputStyle({ radius, dense, size: inputSize }),
            props.disabled && "opacity-50",
            classNames?.input,
          )}
          aria-describedby={hintId}
          {...props}
        />
        {endAdornment}
      </div>
      {hint && (
        <HintComponent
          id={hintId}
          className={cn("mt-1 text-sm opacity-75", classNames?.hint)}
        >
          {hint}
        </HintComponent>
      )}
    </WrapperComponent>
  ),
);
